/* eslint-disable dot-notation */
import { xml2json } from 'xml-js';
import { IXMLJSON } from './types';
import { isArray } from '@agilepoint/utils-common';
import { IAppFiltersFormFields, IAppMetadata, IBasicSettingsFormFields, IPlatformTypesFormFields } from 'modules/create-new-app/components/app-wizard/steps';
import { AuthenticationSettingsResponse, StylingResponseType } from 'modules/shared/functions';
import { IAuthenticationSettingsTextData, IImageMetadata } from 'modules/shared/queries';
import { IMenuOptionsFormFields, IPushNotificationFormFields, IStylingFormFields, MenuOption, PredefinedColors } from 'modules/shared/types';

export const xmlJSONOptions = { compact: true, textKey: 'value', cdataKey: 'cdata', attributesKey: 'attribute', spaces: ' ' };

export const getBasicSettingsXMLMappedValues = (xmlJSON: IXMLJSON, formData: IBasicSettingsFormFields, otherProperties: { mobileAppBaseId: string }) => {
    const appData = xmlJSON.AppData;

    //Change the basic settings value to 'true'
    appData.WizardStepState.BasicSettings.attribute.complete = true;

    //MobileAppCommonSettings
    const mobileAppCommonSettings = appData.MobileAppCommonSettings;

    mobileAppCommonSettings.CompanyInfoEmail.cdata = formData.companyInfoEmail;
    mobileAppCommonSettings.CompanyName.cdata = formData.companyName;
    mobileAppCommonSettings.CompanySupportEmail.cdata = formData.companySupportEmail;
    mobileAppCommonSettings.CompanyWebsite.cdata = formData.companyWebsite;
    mobileAppCommonSettings.CopyRightInfo.cdata = formData.copyrightInfo;
    mobileAppCommonSettings.DefaultLocale.cdata = formData.defaultLocale;
    mobileAppCommonSettings.MobileAppDescription.cdata = formData.mobileAppDescription;
    mobileAppCommonSettings.MobileAppName.cdata = formData.mobileAppName;
    mobileAppCommonSettings.MobileAppVersion.cdata = formData.mobileAppVersion;
    mobileAppCommonSettings.MobileAppBaseId.cdata = otherProperties.mobileAppBaseId;
    mobileAppCommonSettings['EULA'] = {
        cdata: formData.eulaLink
    };
    mobileAppCommonSettings.EULALabel.cdata = formData.eulaText;

    return xmlJSON;
};

export const getBasicSettingsValuesFromXML = (xmlJSON: IXMLJSON) => {
    const mobileAppCommonSettings = xmlJSON.AppData.MobileAppCommonSettings;

    return {
        companyInfoEmail: mobileAppCommonSettings.CompanyInfoEmail.cdata,
        companyName: mobileAppCommonSettings.CompanyName.cdata,
        companySupportEmail: mobileAppCommonSettings.CompanySupportEmail.cdata,
        companyWebsite: mobileAppCommonSettings.CompanyWebsite.cdata,
        copyrightInfo: mobileAppCommonSettings.CopyRightInfo.cdata,
        eulaLink: mobileAppCommonSettings?.EULA?.cdata,
        mobileAppName: mobileAppCommonSettings.MobileAppName.cdata,
        mobileAppDescription: mobileAppCommonSettings.MobileAppDescription.cdata,
        mobileAppVersion: mobileAppCommonSettings.MobileAppVersion.cdata,
        defaultLocale: mobileAppCommonSettings.DefaultLocale.cdata,
        eulaText: mobileAppCommonSettings?.EULALabel?.cdata
    };
};
export const getAuthenticationXMLMappedValues = (xmlJSON: IXMLJSON, parsedAuthData: AuthenticationSettingsResponse, isStepCompleted?: boolean) => {
    const productionURL = xmlJSON.AppData.MobileAppCommonSettings.serverURLs.serverURL.find((item: any) => item.attribute.name === 'Production'); //Gets production node
    productionURL!.cdata = parsedAuthData.ServerConfiguration.ProductionServerInstanceUrl;

    if (isStepCompleted) {
        //Change the authentication settings value to 'true'
        xmlJSON.AppData.WizardStepState.Authentication.attribute.complete = true;
    }

    const authProviders = xmlJSON.AppData.MobileAppCommonSettings.AuthenticationProviders.AuthProvider;

    const pingFedAuthProvider = authProviders.find((item) => item.attribute.Name === 'PingFederate');
    !pingFedAuthProvider && authProviders.push({
        attribute: {
            Name: 'PingFederate',
            ClientId: '',
            Authority: '',
            UsernameClaimType: '',
            Selected: '',
            Default: ''
        }
    });

    const amazonCogAuthProvider = authProviders.find((item) => item.attribute.Name === 'AmazonCognito');
    !amazonCogAuthProvider && authProviders.push({
        attribute: {
            Name: 'AmazonCognito',
            ClientId: '',
            Authority: '',
            UserImpersonationScopeURL: '',
            Selected: '',
            Default: ''
        }
    });

    authProviders?.forEach((item) => {
        switch (item.attribute.Name) {
            case 'ActiveDirectory':
                item.attribute.Domain = parsedAuthData.ActiveDirectoryConfiguration.Domain;
                item.attribute.EnableJWT = parsedAuthData.ActiveDirectoryConfiguration.EnableJWT;
                item.attribute.AudienceURL = parsedAuthData.ActiveDirectoryConfiguration.AudienceURL;
                item.attribute.Selected = parsedAuthData.ActiveDirectoryConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'ActiveDirectoryConfiguration';
                break;
            case 'AgilePointID':
                item.attribute.Selected = parsedAuthData.AgilePiontNXConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'AgilePiontNXConfiguration';
                break;
            case 'IdentityServer':
                item.attribute.ClientId = parsedAuthData.IdentityServerConfiguration.ClientId;
                item.attribute.Authority = parsedAuthData.IdentityServerConfiguration.Authority;
                item.attribute.Selected = parsedAuthData.IdentityServerConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'IdentityServerConfiguration';
                break;
            case 'Okta':
                item.attribute.ClientId = parsedAuthData.OktaConfiguration.ClientId;
                item.attribute.Authority = parsedAuthData.OktaConfiguration.Authority;
                item.attribute.Selected = parsedAuthData.OktaConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'OktaConfiguration';
                break;
            case 'Salesforce':
                item.attribute.ClientId = parsedAuthData.SalesforceConfiguration.ConsumerKey;
                item.attribute.ReturnURL = parsedAuthData.SalesforceConfiguration.ConsumerSecret;
                item.attribute.Selected = parsedAuthData.SalesforceConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'SalesforceConfiguration';
                break;
            case 'WAAD':
                item.attribute.ClientId = parsedAuthData.OfficeConfiguration.ClientId;
                item.attribute.ReturnURL = parsedAuthData.OfficeConfiguration.ReturnURL;
                item.attribute.Resource = parsedAuthData.OfficeConfiguration.Resource;
                item.attribute.UseMSAuthenticatorApp = parsedAuthData.OfficeConfiguration.UseMSAuthenticatorApp;
                item.attribute.Selected = parsedAuthData.OfficeConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'OfficeConfiguration';
                break;
            case 'WAADChina':
                item.attribute.ClientId = parsedAuthData.OfficeConfigurationChina.ClientId;
                item.attribute.ReturnURL = parsedAuthData.OfficeConfigurationChina.ReturnURL;
                item.attribute.Resource = parsedAuthData.OfficeConfigurationChina.Resource;
                item.attribute.UseMSAuthenticatorApp = parsedAuthData.OfficeConfigurationChina.UseMSAuthenticatorApp;
                item.attribute.Selected = parsedAuthData.OfficeConfigurationChina.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'OfficeConfigurationChina';
                break;
            case 'Google':
                item.attribute.ClientId = parsedAuthData.GoogleConfiguration.ClientId;
                item.attribute.ReturnURL = parsedAuthData.GoogleConfiguration.ReturnURL;
                item.attribute.Resource = parsedAuthData.GoogleConfiguration.Resource;
                item.attribute.Selected = parsedAuthData.GoogleConfiguration.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'GoogleConfiguration';
                break;
            case 'PingFederate':
                item.attribute.ClientId = parsedAuthData?.PingFederateConfiguration?.ClientId;
                item.attribute.Authority = parsedAuthData?.PingFederateConfiguration?.Authority;
                item.attribute.UsernameClaimType = parsedAuthData?.PingFederateConfiguration?.UsernameClaimType;
                item.attribute.Selected = parsedAuthData?.PingFederateConfiguration?.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'PingFederateConfiguration';
                break;
            case 'AmazonCognito':
                item.attribute.ClientId = parsedAuthData?.AmazonCognitoConfiguration?.ClientId;
                item.attribute.Authority = parsedAuthData?.AmazonCognitoConfiguration?.Authority;
                item.attribute.UserImpersonationScopeURL = parsedAuthData?.AmazonCognitoConfiguration?.UserImpersonationScopeURL;
                item.attribute.Selected = parsedAuthData?.AmazonCognitoConfiguration?.isSelected;
                item.attribute.Default = parsedAuthData.defaultConfig === 'AmazonCognitoConfiguration';
                break;
            default: break;
        }
    });
    return xmlJSON;
};

const setDefaultValue = (currentDefaultValue: string | boolean, accumulatedValues: IAuthenticationSettingsTextData, key: string) => { if (currentDefaultValue === 'true') { accumulatedValues.defaultConfig = key; } };

export const getAuthenticationValuesFromXML = (xmlJSON: IXMLJSON): IAuthenticationSettingsTextData => {
    const authProviders = xmlJSON.AppData.MobileAppCommonSettings.AuthenticationProviders;
    const productionURL = xmlJSON.AppData.MobileAppCommonSettings.serverURLs.serverURL.find((item) => item.attribute.name === 'Production'); //Gets production node

    const result = authProviders.AuthProvider.reduce((accumulatedValues, currentValue) => {
        switch (currentValue.attribute.Name) {
            case 'ActiveDirectory':
                accumulatedValues['activeDirectoryConfiguration'] = {
                    domain: currentValue.attribute.Domain,
                    enableJWT: currentValue.attribute.EnableJWT,
                    audienceURL: currentValue.attribute.AudienceURL,
                    isSelected: currentValue.attribute.Selected === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'ActiveDirectoryConfiguration');
                break;
            case 'AgilePointID':
                accumulatedValues['agilePiontNXConfiguration'] = {
                    isSelected: currentValue.attribute.Selected === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'AgilePiontNXConfiguration');
                break;
            case 'IdentityServer':
                accumulatedValues['identityServerConfiguration'] = {
                    clientId: currentValue.attribute.ClientId,
                    authority: currentValue.attribute.Authority,
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'IdentityServerConfiguration');
                break;
            case 'Okta':
                accumulatedValues['oktaConfiguration'] = {
                    clientId: currentValue.attribute.ClientId,
                    authority: currentValue.attribute.Authority,
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'OktaConfiguration');
                break;
            case 'Salesforce':
                accumulatedValues['salesforceConfiguration'] = {
                    consumerKey: currentValue.attribute.ClientId,
                    consumerSecret: currentValue.attribute.ReturnURL,
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'SalesforceConfiguration');
                break;
            case 'WAAD':
                accumulatedValues['officeConfiguration'] = {
                    clientId: currentValue.attribute.ClientId,
                    resource: currentValue.attribute.Resource,
                    returnURL: currentValue.attribute.ReturnURL,
                    useMSAuthenticatorApp: currentValue.attribute.UseMSAuthenticatorApp === 'true',
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'OfficeConfiguration');
                break;
            case 'WAADChina':
                accumulatedValues['officeConfigurationChina'] = {
                    clientId: currentValue.attribute.ClientId,
                    resource: currentValue.attribute.Resource,
                    returnURL: currentValue.attribute.ReturnURL,
                    useMSAuthenticatorApp: currentValue.attribute.UseMSAuthenticatorApp === 'true',
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'OfficeConfigurationChina');
                break;
            case 'Google':
                accumulatedValues['googleConfiguration'] = {
                    clientId: currentValue.attribute.ClientId,
                    returnURL: currentValue.attribute.ReturnURL,
                    resource: currentValue.attribute.Resource,
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'GoogleConfiguration');
                break;
            case 'PingFederate':
                accumulatedValues['pingFederateConfiguration'] = {
                    clientId: currentValue.attribute.ClientId,
                    authority: currentValue.attribute.Authority,
                    usernameClaimType: currentValue.attribute.UsernameClaimType,
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'PingFederateConfiguration');
                break;
            case 'AmazonCognito':
                accumulatedValues['amazonCognitoConfiguration'] = {
                    clientId: currentValue.attribute.ClientId,
                    authority: currentValue.attribute.Authority,
                    userImpersonationScopeURL: currentValue.attribute.UserImpersonationScopeURL,
                    isSelected: (currentValue.attribute.Selected) === 'true'
                };
                setDefaultValue(currentValue.attribute.Default, accumulatedValues, 'AmazonCognitoConfiguration');
                break;
            default: break;
        }

        return accumulatedValues;
    }, {} as IAuthenticationSettingsTextData);

    result['serverConfiguration'] = {
        productionServerInstanceUrl: productionURL?.cdata!
    };

    return result;
};

export const getAppFiltersXMLMappedValues = (xmlJSON: IXMLJSON, formData: IAppFiltersFormFields, isStepCompleted?: boolean) => {
    const { appListOptions, showApps } = formData;

    if (isStepCompleted) {
        //Change the ApplicationFilters settings value to 'true'
        xmlJSON.AppData.WizardStepState.ApplicationFilters.attribute.complete = true;
    }

    if (appListOptions === 'show-only-selected-below') {
        xmlJSON.AppData.MobileAppCommonSettings.ApplicationFilters.attribute.Enabled = 'true';

        const filterValues = Object.entries(showApps).filter(([, value]) => value === true).map(([filterName]) => ({ value: filterName }));
        xmlJSON.AppData.MobileAppCommonSettings.ApplicationFilters.Filter = filterValues;
    }
    else {
        xmlJSON.AppData.MobileAppCommonSettings.ApplicationFilters.attribute.Enabled = 'false';
        xmlJSON.AppData.MobileAppCommonSettings.ApplicationFilters.Filter = [];
    }
    return xmlJSON;
};

export const getAppFiltersFromXML = (xmlJSON: IXMLJSON, appListAPIData: IAppMetadata[]) => {
    const appFilters = xmlJSON.AppData.MobileAppCommonSettings.ApplicationFilters;
    let result = [] as IAppMetadata[];
    let appListOptions: 'show-only-selected-below' | 'show-all' = 'show-all';

    if (appFilters.attribute.Enabled === 'true') {
        appListOptions = 'show-only-selected-below';
        const appFiltersArrayCheck = isArray(appFilters?.Filter) ? appFilters?.Filter : [appFilters?.Filter];
        const filtersArray = appFilters?.Filter === undefined ? [] : appFiltersArrayCheck;
        result = filtersArray?.map((item) => {
            const foundItem = appListAPIData.find((apiItem) => apiItem.name === item.value);
            if (foundItem) {
                return foundItem;
            }
            return { appID: '', description: '', guid: 0, name: item.value, value: item.value };
        });
    } else {
        result = appListAPIData;
    }

    return {
        appListOptions,
        showApps: result.reduce((acc, curr) => {
            acc[curr.name] = true;
            return acc;
        }, {})
    };
};

export const getMenuOptionsXMLMappedValues = (xmlJSON: IXMLJSON, parsedAppMenus: MenuOption[] | IMenuOptionsFormFields, isStepCompleted?: boolean) => {
    if (isStepCompleted) {
        //Change the MenuOptions settings value to 'true'
        xmlJSON.AppData.WizardStepState.MenuOptions.attribute.complete = true;
    }

    if (isArray(parsedAppMenus)) {
        //Backward compaitable code
        const menusToBeInjected = parsedAppMenus.map((menu) => ({
            attribute: {
                Name: menu.Name,
                Enabled: menu.isEnabled,
                DisplayName: menu.Value,
                IsLocalized: 'false'
            }
        }));

        const newFormat = [
            {
                attribute: { 'locale': 'default' },
                Menu: menusToBeInjected
            }
        ];

        xmlJSON.AppData.MobileAppCommonSettings.MobileAppMenu = newFormat;
        return xmlJSON;
    }

    const arrayLikeObject = Array.from(Object.entries(parsedAppMenus.menuOptions), ([locales, menuOptions]) => ({
        locales,
        menuOptions: Array.from(Object.entries(menuOptions), ([menuName, { fieldValue, isEnabled }]) => ({
            attribute: {
                Name: menuName,
                Enabled: isEnabled,
                DisplayName: fieldValue,
                IsLocalized: 'true'
            }
        }))
    }));
    xmlJSON.AppData.MobileAppCommonSettings.MobileAppMenu = arrayLikeObject.map((menuObj) => ({
        attribute: {
            locale: menuObj.locales
        },
        Menu: menuObj.menuOptions
    }));
    return xmlJSON;
};

export const getMenuOptionsFromXML = (xmlJSON: IXMLJSON): MenuOption[] | IMenuOptionsFormFields => {
    if (!isArray(xmlJSON.AppData.MobileAppCommonSettings.MobileAppMenu)) {
        const menus = xmlJSON.AppData.MobileAppCommonSettings.MobileAppMenu.Menu;
        return menus.map((item) => ({ isEnabled: item.attribute.Enabled === 'true', Name: item.attribute.Name, Value: item.attribute.DisplayName }));
    }

    const res = {} as IMenuOptionsFormFields;
    res.locales = xmlJSON.AppData.MobileAppCommonSettings.MobileAppMenu[0].attribute.locale;
    res.menuOptions = xmlJSON.AppData.MobileAppCommonSettings.MobileAppMenu.reduce((acc, curr) => {
        acc[curr.attribute.locale] = curr.Menu.reduce((_acc, _curr) => {
            _acc[_curr.attribute.Name] = {
                'isEnabled': _curr.attribute.Enabled,
                'fieldValue': _curr.attribute.DisplayName
            };
            return _acc;
        }, {});
        return acc;
    }, {});

    return res;
};

export const getPlatformCertificateXMLMappedValues = (xmlJSON: IXMLJSON, formData: IPlatformTypesFormFields, certificateDetails: { androidCertificateId?: string, iosP12CertificateId?: string, iosProvisioningCertificateId?: string }) => {
    const { Android, iOS, platformTypes } = formData;

    //Change the Platform settings value to 'true'
    xmlJSON.AppData.WizardStepState.Platform.attribute.complete = true;

    //Android
    if (platformTypes.includes('Android')) {
        //Setting android atrribute to true
        xmlJSON.AppData.MobileAppPlatform.attribute.Android = 'true';
        xmlJSON.AppData.MobileAppPlatform.attribute.IOS = platformTypes.includes('iOS') ? 'true' : 'false';

        const androidNode = xmlJSON.AppData.AndroidAppSettings;
        androidNode.MobileBuildType.value = Android.buildTypes;
        androidNode.PackageName.value = Android.packageName;

        if (Android.buildTypes === 'Store') {
            androidNode.StoreCertificate.CertificateFile.value = certificateDetails?.androidCertificateId;
            androidNode.StoreCertificate.CertificateFileName.value = Android.certificate.selectedFiles[0].name;
            androidNode.StoreCertificate.storePassword.cdata = Android.storePassword;
            androidNode.StoreCertificate.keyAlias.cdata = Android.keyAlias;
            androidNode.StoreCertificate.keyPassword.cdata = Android.keyPassword;
        }
    }

    //IOS
    if (platformTypes.includes('iOS')) {
        //Setting ios atrribute to true
        xmlJSON.AppData.MobileAppPlatform.attribute.IOS = 'true';
        xmlJSON.AppData.MobileAppPlatform.attribute.Android = platformTypes.includes('Android') ? 'true' : 'false';

        const iosNode = xmlJSON.AppData.IOSAppSettings;
        iosNode.MobileBuildType.value = iOS.buildTypes;
        iosNode.StoreCertificate.CertificateFile.value = certificateDetails?.iosP12CertificateId;
        iosNode.StoreCertificate.CertificateFileName.value = iOS.certificate?.selectedFiles[0].name;
        iosNode.StoreCertificate.Password.cdata = iOS.password;
        iosNode.StoreCertificate.ProvisioningProfile.value = certificateDetails?.iosProvisioningCertificateId;
        iosNode.StoreCertificate.ProvisioningProfileCertificateFileName.value = iOS.provisioningProfile?.selectedFiles[0].name;
    }

    return xmlJSON;
};

export const setPlatformValuesToXML = (xmlJSON: IXMLJSON, certificateDetails: { androidCertificateId?: string, iosP12CertificateId?: string, iosProvisioningCertificateId?: string }) => {
    const iosNode = xmlJSON.AppData.IOSAppSettings;

    if (certificateDetails.iosP12CertificateId) {
        iosNode.StoreCertificate.CertificateFile.value = certificateDetails?.iosP12CertificateId;
    }
    if (certificateDetails.iosProvisioningCertificateId) {
        iosNode.StoreCertificate.ProvisioningProfile.value = certificateDetails?.iosProvisioningCertificateId;
    }
    if (certificateDetails.androidCertificateId) {
        xmlJSON.AppData.AndroidAppSettings.StoreCertificate.CertificateFile.value = certificateDetails?.androidCertificateId;
    }
    return xmlJSON;
};

export const getPlatformValuesFromXML = (xmlJSON: IXMLJSON) => {
    const android = xmlJSON.AppData.MobileAppPlatform.attribute.Android;
    const ios = xmlJSON.AppData.MobileAppPlatform.attribute.IOS;
    const platformTypes = [];
    const platformData: Omit<IPlatformTypesFormFields, 'platformTypes'> = {
        Android: {
            buildTypes: 'MDM',
            packageName: '',
            certificate: {
                action: 'add',
                changedFileIds: [''],
                selectedFiles: []
            },
            keyAlias: '',
            keyPassword: '',
            storePassword: ''
        },
        iOS: {
            buildTypes: 'MDM',
            certificate: {
                action: 'add',
                changedFileIds: [],
                selectedFiles: []
            },
            password: undefined,
            provisioningProfile: {
                action: 'add',
                changedFileIds: [],
                selectedFiles: []
            }
        }
    };

    if (android === 'true') {
        platformTypes.push('Android');

        const androidNode = xmlJSON.AppData.AndroidAppSettings;
        platformData.Android.packageName = androidNode.PackageName.value;
        platformData.Android.buildTypes = androidNode.MobileBuildType.value;

        if (androidNode.MobileBuildType.value === 'Store') {
            platformData.Android.certificate.selectedFiles.push({
                name: androidNode.StoreCertificate.CertificateFileName.value,
                id: androidNode.StoreCertificate.CertificateFile.value!,
                type: '',
                size: 0
            });
            platformData.Android.storePassword = androidNode.StoreCertificate.storePassword.cdata;
            platformData.Android.keyAlias = androidNode.StoreCertificate.keyAlias.cdata;
            platformData.Android.keyPassword = androidNode.StoreCertificate.keyPassword.cdata;
        }
    }

    if (ios === 'true') {
        platformTypes.push('iOS');

        const iosNode = xmlJSON.AppData.IOSAppSettings;
        platformData.iOS.buildTypes = iosNode.MobileBuildType.value!;
        platformData.iOS.certificate!.selectedFiles.push({
            name: iosNode.StoreCertificate.CertificateFileName.value!,
            id: iosNode.StoreCertificate.CertificateFile.value!,
            type: '',
            size: 0
        });
        platformData.iOS.password = iosNode.StoreCertificate.Password.cdata;
        platformData.iOS.provisioningProfile!.selectedFiles.push({
            name: iosNode.StoreCertificate.ProvisioningProfileCertificateFileName.value!,
            id: iosNode.StoreCertificate.ProvisioningProfile.value!,
            type: '',
            size: 0
        });
    }

    return {
        platformTypes,
        ...platformData
    };
};

export const getApplicationThemeXMLMappedValues = (xmlJSON: IXMLJSON, parsedAppThemeValues: StylingResponseType, isStepCompleted?: boolean) => {

    if (isStepCompleted) {
        xmlJSON.AppData.WizardStepState.Theme.attribute.complete = true;
    }

    const androidAppThemeAttributes = xmlJSON.AppData.AndroidAppSettings.ApplicationTheme.attribute;
    androidAppThemeAttributes.AppBackgroundColor = parsedAppThemeValues.menuColorCode!;
    androidAppThemeAttributes.MenuBackgroundColor = parsedAppThemeValues.menuColorCode!;
    androidAppThemeAttributes.ThemeName = parsedAppThemeValues.previewImage;

    const iosAppThemeAttributes = xmlJSON.AppData.IOSAppSettings.ApplicationTheme.attribute;
    iosAppThemeAttributes.AppBackgroundColor = parsedAppThemeValues.menuColorCode!;
    iosAppThemeAttributes.MenuBackgroundColor = parsedAppThemeValues.menuColorCode!;
    iosAppThemeAttributes.ThemeName = parsedAppThemeValues.previewImage;

    const windowsAppThemeAttributes = xmlJSON.AppData.WindowsAppSettings.ApplicationTheme.attribute;
    windowsAppThemeAttributes.AppBackgroundColor = parsedAppThemeValues.menuColorCode!;
    windowsAppThemeAttributes.MenuBackgroundColor = parsedAppThemeValues.menuColorCode!;
    windowsAppThemeAttributes.ThemeName = parsedAppThemeValues.previewImage;
    return xmlJSON;
};

export const getStylingThemeValuesFromXML = (xmlJSON: IXMLJSON): IStylingFormFields => {
    const appThemeNode = xmlJSON.AppData.AndroidAppSettings.ApplicationTheme;

    const getColor = () => {
        switch (appThemeNode.attribute.ThemeName) {
            case 'Blue':
                return PredefinedColors.Blue;
            case 'Orange':
                return PredefinedColors.Orange;
            case 'Green':
                return PredefinedColors.Green;
            case 'Red':
                return PredefinedColors.Red;
            default: return undefined;
        }
    };

    return {
        appStyles: appThemeNode.attribute.ThemeName === 'Custom' ? 'customColor' : 'predefinedColor',
        customColor: {
            buttonColor: appThemeNode.attribute.ButtonColor,
            menuColor: appThemeNode.attribute.MenuBackgroundColor
        },
        predefinedColor: {
            color: getColor()

        }
    };
};

export const getUploadImagesXMLMappedValues = (xmlJSON: IXMLJSON, parsedUploadImages: { ID: string, Name: string }[], isStepCompleted?: boolean) => {

    if (isStepCompleted) {
        //Change the UploadImages settings value to 'true'
        xmlJSON.AppData.WizardStepState.UploadImages.attribute.complete = true;
    }

    const reducedImages = parsedUploadImages.reduce((acc, curr) => {
        acc[curr.Name] = curr.ID;
        return acc;
    }, {});


    //for android:
    const androidMobileAppImages = xmlJSON.AppData.AndroidAppSettings.MobileAppImages;

    //xxhdpi
    androidMobileAppImages.xxhdpi.IconFile.value = reducedImages['Android_xxhdpi_Icon'];
    androidMobileAppImages.xxhdpi.Logo.value = reducedImages[''];
    androidMobileAppImages.xxhdpi.BackgroundImage.value = reducedImages['Android_xxhdpi_Background'];
    androidMobileAppImages.xxhdpi.StoreLogo.value = reducedImages[''];
    androidMobileAppImages.xxhdpi.WhiteLogo.value = reducedImages['Android_xxhdpi_Background'];

    //xhdpi
    androidMobileAppImages.xhdpi.IconFile.value = reducedImages['Android_xhdpi_Icon'];
    androidMobileAppImages.xhdpi.Logo.value = reducedImages['Android_xhdpi_Logo'];
    androidMobileAppImages.xhdpi.BackgroundImage.value = reducedImages['Android_xhdpi_Background'];
    androidMobileAppImages.xhdpi.StoreLogo.value = reducedImages[''];
    androidMobileAppImages.xhdpi.WhiteLogo.value = reducedImages['Android_xhdpi_Background'];

    //hdpi
    androidMobileAppImages.hdpi.IconFile.value = reducedImages['Android_hdpi_Icon'];
    androidMobileAppImages.hdpi.Logo.value = reducedImages['Android_hdpi_Logo'];
    androidMobileAppImages.hdpi.BackgroundImage.value = reducedImages['Android_hdpi_Background'];
    androidMobileAppImages.hdpi.StoreLogo.value = reducedImages[''];
    androidMobileAppImages.hdpi.WhiteLogo.value = reducedImages['Android_hdpi_Background'];

    //land-hdpi
    androidMobileAppImages['land-hdpi'].IconFile.value = reducedImages[''];
    androidMobileAppImages['land-hdpi'].Logo.value = reducedImages[''];
    androidMobileAppImages['land-hdpi'].BackgroundImage.value = reducedImages['Android_land-hdpi_Background'];
    androidMobileAppImages['land-hdpi'].StoreLogo.value = reducedImages[''];

    //land-xhdpi
    androidMobileAppImages['land-xhdpi'].IconFile.value = reducedImages[''];
    androidMobileAppImages['land-xhdpi'].Logo.value = reducedImages[''];
    androidMobileAppImages['land-xhdpi'].BackgroundImage.value = reducedImages['Android_land-xhdpi_Background'];
    androidMobileAppImages['land-xhdpi'].StoreLogo.value = reducedImages[''];


    //for ios
    const iosMobileAppImages = xmlJSON.AppData.IOSAppSettings.MobileAppImages;

    if (iosMobileAppImages?.UniversalIcon?.AppIcon) {
        iosMobileAppImages.UniversalIcon.AppIcon.value = reducedImages['iOS_AppDefaultIcon'];
        iosMobileAppImages.UniversalIcon.SplashScreen.value = reducedImages['iOS_SplashScreeniphone'];
        iosMobileAppImages.UniversalIcon.ApplicationLogo.value = reducedImages['iOS_AppApplicationLogo'];
    } else {
        iosMobileAppImages.UniversalIcon = {
            ...iosMobileAppImages.UniversalIcon,
            AppIcon: {
                value: reducedImages['iOS_AppDefaultIcon']
            },
            SplashScreen: {
                value: reducedImages['iOS_SplashScreeniphone']
            },
            ApplicationLogo: {
                value: reducedImages['iOS_AppApplicationLogo']
            }
        };
    }

    return xmlJSON;
};

export const getPushNotificationsXMLMappedValue = (xmlJSON: IXMLJSON, pushNotifications: IPushNotificationFormFields, isStepCompleted?: boolean) => {
    if (xmlJSON.AppData.WizardStepState.PushNotifications) {
        if (isStepCompleted) {
            //Change the Push Notifiction settings value to 'true'
            xmlJSON.AppData.WizardStepState.PushNotifications.attribute.complete = true;
        }

        const pushNotificationTag = xmlJSON.AppData.PushNotification;

        pushNotificationTag.HostUrl.value = pushNotifications.hostUrl;
        pushNotificationTag.Domain.value = pushNotifications.domain;
        pushNotificationTag.Value1.value = pushNotifications.value1;
        pushNotificationTag.Value2.value = pushNotifications.value2;

        return xmlJSON;
    }
    //NOTE: Below code is for existing apps which do not have push notification tag in the xml
    xmlJSON.AppData.WizardStepState['PushNotifications'] = {
        attribute: {
            complete: true
        }
    };
    xmlJSON.AppData['PushNotification'] = {
        HostUrl: {
            value: pushNotifications.hostUrl
        },
        Domain: {
            value: pushNotifications.domain
        },
        Value1: {
            value: pushNotifications.value1
        },
        Value2: {
            value: pushNotifications.value2
        }
    };
    return xmlJSON;
};

export const getPushNotificationsValuesFromXML = (xmlJSON: IXMLJSON): IPushNotificationFormFields => {
    const appThemeNode = xmlJSON.AppData.PushNotification;

    return {
        domain: appThemeNode?.Domain.value,
        hostUrl: appThemeNode?.HostUrl.value,
        value1: appThemeNode?.Value1.value,
        value2: appThemeNode?.Value2.value
    };
};

export const getUploadedImageInfoFromXML = (xmlString?: string): IImageMetadata[] | undefined => {
    let selectedIDs: IImageMetadata[] | undefined;
    if (xmlString) {
        const parsedXMLJSON = JSON.parse(xml2json(xmlString, xmlJSONOptions)) as IXMLJSON;
        const resultObject = {};

        //Android
        const androidImageNode = parsedXMLJSON.AppData.AndroidAppSettings.MobileAppImages;
        resultObject['Android_xxhdpi_Icon'] = androidImageNode.xxhdpi.IconFile.value;
        resultObject['Android_xhdpi_Icon'] = androidImageNode.xhdpi.IconFile.value;
        resultObject['Android_xhdpi_Logo'] = androidImageNode.xhdpi.Logo.value;
        resultObject['Android_xhdpi_Background'] = androidImageNode.xhdpi.BackgroundImage.value;
        resultObject['Android_hdpi_Icon'] = androidImageNode.hdpi.IconFile.value;
        resultObject['Android_hdpi_Logo'] = androidImageNode.hdpi.Logo.value;
        resultObject['Android_hdpi_Background'] = androidImageNode.hdpi.BackgroundImage.value;
        resultObject['Android_land-hdpi_Background'] = androidImageNode['land-hdpi'].BackgroundImage.value;
        resultObject['Android_land-xhdpi_Background'] = androidImageNode['land-xhdpi'].BackgroundImage.value;
        resultObject['Android_xxhdpi_Background'] = androidImageNode.xxhdpi.BackgroundImage.value;

        //IOS
        const iosImageNode = parsedXMLJSON.AppData.IOSAppSettings.MobileAppImages;

        resultObject['iOS_AppDefaultIcon'] = iosImageNode.UniversalIcon?.AppIcon.value;
        resultObject['iOS_SplashScreeniphone'] = iosImageNode.UniversalIcon?.SplashScreen.value;
        resultObject['iOS_AppApplicationLogo'] = iosImageNode.UniversalIcon?.ApplicationLogo.value;

        selectedIDs = [];
        for (const [key, value] of Object.entries(resultObject)) {
            if (value) {
                selectedIDs.push({ ID: value as string, Name: key });
            }
        }

    }
    return selectedIDs;
};

export const getBuildAppXMLMappedValues = (xmlJSON: IXMLJSON) => {
    xmlJSON.AppData.WizardStepState.BasicSettings.attribute.complete = true;
    return xmlJSON;
};
